/** Read-only wire model. Missing fields in old shipments never acquire form defaults. */
export type ShipmentCharacteristicsData = {
  transport_format?: 'dedicated' | 'partial' | 'groupage'
  dimensions_scope?: 'total' | 'pallet' | 'package'
  cargo_name?: string
  cargo_weight_kg?: number
  cargo_volume_m3?: number
  pallet_count?: number
  package_count?: number
  cargo_length_cm?: number
  cargo_width_cm?: number
  cargo_height_cm?: number
  body_volume_m3?: number
}

const measurementKeys = ['cargo_weight_kg', 'cargo_volume_m3', 'cargo_length_cm', 'cargo_width_cm', 'cargo_height_cm', 'body_volume_m3'] as const
const countKeys = ['pallet_count', 'package_count'] as const

function text(value: unknown): string | undefined {
  return typeof value === 'string' ? value.trim() || undefined : undefined
}

function number(value: unknown): number | undefined {
  // Accept older JSON string scalars, but never coerce booleans, blanks or objects to numbers.
  if (typeof value !== 'number' && !(typeof value === 'string' && /^\d+(?:[.,]\d+)?$/.test(value.trim()))) return undefined
  const result = typeof value === 'number' ? value : Number(value.trim().replace(',', '.'))
  return Number.isFinite(result) ? result : undefined
}

export function readShipmentCharacteristics(raw: Record<string, unknown>): ShipmentCharacteristicsData {
  const data: ShipmentCharacteristicsData = { transport_format: undefined, dimensions_scope: undefined }
  if (raw.transport_format === 'dedicated' || raw.transport_format === 'partial' || raw.transport_format === 'groupage') data.transport_format = raw.transport_format
  if (raw.dimensions_scope === 'total' || raw.dimensions_scope === 'pallet' || raw.dimensions_scope === 'package') data.dimensions_scope = raw.dimensions_scope
  data.cargo_name = text(raw.cargo_name)
  for (const key of measurementKeys) {
    const value = number(raw[key])
    data[key] = value !== undefined && value > 0 ? value : undefined
  }
  for (const key of countKeys) {
    const value = number(raw[key])
    data[key] = value !== undefined && Number.isSafeInteger(value) && value >= 0 ? value : undefined
  }
  return data
}

export type ShipmentInformationRow = { label: string; value: string }

/** Build sections directly from received route_json; no calculated volume or fabricated format. */
export function getShipmentInformation(routeJson: unknown, vehicleLabel: (code: string) => string = code => code) {
  let raw: Record<string, unknown> = {}
  try {
    const parsed = typeof routeJson === 'string' ? JSON.parse(routeJson) : routeJson
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) raw = parsed
  } catch { /* An invalid legacy blob simply has no optional sections. */ }
  const data = readShipmentCharacteristics(raw)
  const transport: ShipmentInformationRow[] = []
  const cargo: ShipmentInformationRow[] = []
  const add = (rows: ShipmentInformationRow[], label: string, value: string | undefined) => {
    if (value) rows.push({ label, value })
  }
  const numeric = (value: number | undefined, unit = '') => value === undefined ? undefined : `${value.toLocaleString('ru-RU', { maximumFractionDigits: 20 })}${unit}`
  const formats = { dedicated: 'Отдельный транспорт', partial: 'Догруз', groupage: 'Сборный груз' }
  add(transport, 'Формат перевозки', data.transport_format ? formats[data.transport_format] : undefined)
  const type = text(raw.vehicle_type)
  add(transport, 'Тип транспорта', type ? vehicleLabel(type) : undefined)
  add(transport, 'Объём кузова', numeric(data.body_volume_m3, ' м³'))
  for (const [key, label] of [['vehicle_number', 'Номер автомобиля'], ['trailer_number', 'Номер полуприцепа'], ['driver_name', 'Водитель'], ['company', 'Компания-перевозчик']] as const) add(transport, label, text(raw[key]))
  add(transport, 'Телефон водителя', text(raw.driver_phone) || text(raw.contact))
  add(cargo, 'Наименование груза', data.cargo_name)
  add(cargo, 'Вес', numeric(data.cargo_weight_kg, ' кг'))
  add(cargo, 'Объём', numeric(data.cargo_volume_m3, ' м³'))
  add(cargo, 'Количество паллет', numeric(data.pallet_count))
  add(cargo, 'Количество грузовых мест', numeric(data.package_count))
  if (data.dimensions_scope) {
    const labels = { total: 'Габариты всего груза', pallet: 'Габариты одной паллеты', package: 'Габариты одного грузового места' }
    const dimensions = [data.cargo_length_cm, data.cargo_width_cm, data.cargo_height_cm]
    const value = dimensions.every(v => v !== undefined)
      ? `${dimensions.map(v => numeric(v)).join(' × ')} см`
      : dimensions.map((v, index) => v === undefined ? '' : `${['Длина', 'Ширина', 'Высота'][index]} ${numeric(v, ' см')}`).filter(Boolean).join(', ')
    add(cargo, labels[data.dimensions_scope], value)
  }
  return { transport, cargo }
}
